'use client';

import AuthProviderButton from '@/features/auth/components/AuthProviderButton';
import GoogleAuthButton from '@/features/auth/google/GoogleAuthButton';
import { useGoogleAuth } from '@/features/auth/google/useGoogleAuth';
import { useOAuthPopupAuth } from '@/features/auth/oauth/useOAuthPopupAuth';
import { buildAuthHref } from '@/features/auth/utils/authRedirect';
import { getSafeAuthClientMessage } from '@/features/auth/utils/getSafeAuthClientMessage';
import { FormField, FormGrid } from '@/features/forms/FormPrimitives';
import Message from '@/features/primitives/Message';
import { clientFetch } from '@/lib/api/clientFetch';
import { motion, useReducedMotion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { ChevronRight, Discord } from 'react-bootstrap-icons';

function readLoginMessage(messages, path, fallback = '') {
  const resolved = String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((current, segment) => current?.[segment], messages?.loginScreen);

  return typeof resolved === 'string' ? resolved : fallback;
}

export default function LoginForm({
  redirectTarget = '/account',
  messages = {},
  title = 'Sign In',
  brandMarkAlt = 'Login',
}) {
  const router = useRouter();
  const targetPath = redirectTarget || '/account';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const reduceMotion = useReducedMotion();
  const loginMessage = (path, fallback = '') =>
    readLoginMessage(messages, path, fallback);
  const loginErrors = {
    generic: loginMessage('errors.generic', 'Unable to sign in.'),
    invalidCredentials: loginMessage(
      'errors.invalidCredentials',
      'Email or password is incorrect.',
    ),
    tooManyAttempts: loginMessage(
      'errors.tooManyAttempts',
      'Too many attempts. Please wait a moment and try again.',
    ),
    popupBlocked: loginMessage(
      'errors.popupBlocked',
      'Authentication popup was blocked. Please allow popups and try again.',
    ),
    googleUnavailable: loginMessage(
      'errors.googleUnavailable',
      'Google sign-in is unavailable right now.',
    ),
    googleLoadFailed: loginMessage(
      'errors.googleLoadFailed',
      'Google sign-in could not load. Please try again.',
    ),
    googleIncomplete: loginMessage(
      'errors.googleIncomplete',
      'Google sign-in did not complete. Please try again.',
    ),
    googleFailed: loginMessage(
      'errors.googleFailed',
      'Google sign-in failed. Please try again.',
    ),
    discordUnavailable: loginMessage(
      'errors.discordUnavailable',
      'Discord sign-in is unavailable right now.',
    ),
    discordIncomplete: loginMessage(
      'errors.discordIncomplete',
      'Discord sign-in did not complete. Please try again.',
    ),
    discordFailed: loginMessage(
      'errors.discordFailed',
      'Discord sign-in failed. Please try again.',
    ),
  };
  const navigateAfterAuth = () => {
    if (typeof window !== 'undefined') {
      window.location.replace(targetPath);
      return;
    }

    router.replace(targetPath);
    router.refresh();
  };
  const handleProviderError = (fallbackMessage) => (error) =>
    setErrorMessage(
      getSafeAuthClientMessage(error, {
        fallbackMessage,
        messages: loginErrors,
      }),
    );
  const googleAuth = useGoogleAuth({
    redirectTarget,
    onError: handleProviderError(loginErrors.googleUnavailable),
    onSuccess: async () => {
      navigateAfterAuth();
    },
  });
  const discordAuth = useOAuthPopupAuth({
    provider: 'discord',
    redirectTarget,
    onError: handleProviderError(loginErrors.discordUnavailable),
    onSuccess: async () => {
      navigateAfterAuth();
    },
  });
  const isPopupAuthBusy = discordAuth.isLoading;
  // Discord is a hub-specific auth provider; only show it when the user
  // is navigating within /hub routes.
  const showDiscord = Boolean(redirectTarget?.startsWith('/hub'));

  const containerVariants = {
    hidden: {
      opacity: 0,
      y: reduceMotion ? 0 : 8,
      scale: reduceMotion ? 1 : 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: reduceMotion ? 0 : 0.2, ease: 'easeOut' },
    },
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    const nextEmail = email.trim();
    const nextPassword = password;

    try {
      await clientFetch('/api/users/auth', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          email: nextEmail,
          password: nextPassword,
        }),
      });

      navigateAfterAuth();
    } catch (error) {
      setErrorMessage(
        getSafeAuthClientMessage(error, {
          fallbackMessage: loginErrors.generic,
          messages: loginErrors,
        }),
      );
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    router.replace('/shop');
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <Modal
      show
      onHide={handleClose}
      centered
      animation
      backdrop="static"
      backdropClassName="auth-glass-backdrop"
      dialogClassName="auth-glass-modal login-glass-modal"
      contentClassName="auth-glass-modal__content"
      aria-labelledby="login-legend"
    >
      <Modal.Body>
        <button
          type="button"
          className="auth-glass-modal__close"
          onClick={handleClose}
          aria-label={loginMessage('closeLabel', 'Close')}
        />

        <div className="auth-surface-wrapper subtotal-card-wrapper">
          <div className="checkout-summary checkout-summary--section auth-surface-card">
            <div className="auth-surface-body">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="auth-modal auth-glass-modal__inner"
              >
                <form className="auth-form" onSubmit={handleSubmit} noValidate>
                  <fieldset id="login-group" aria-labelledby="login-legend">
                    <div className="auth-brand-mark" aria-hidden="true">
                      <img
                        src="/icons/login.png"
                        alt={brandMarkAlt}
                        width="40"
                        height="40"
                        className="profile-avatar-img"
                      />
                    </div>

                    <legend id="login-legend" className="form-legend mt-n2">
                      {title}
                    </legend>

                    <div
                      className={`auth-provider-grid${showDiscord ? '' : ' auth-provider-grid--single'}`}
                    >
                      <GoogleAuthButton
                        onClick={googleAuth.signIn}
                        disabled={
                          isSubmitting ||
                          googleAuth.isLoading ||
                          isPopupAuthBusy
                        }
                        label={loginMessage(
                          'googleCta',
                          'Continue with Google',
                        )}
                      />

                      {showDiscord && (
                        <AuthProviderButton
                          provider="discord"
                          onClick={discordAuth.signIn}
                          disabled={isSubmitting || isPopupAuthBusy}
                          label={loginMessage(
                            'discordCta',
                            'Continue with Discord',
                          )}
                          icon={<Discord />}
                        />
                      )}
                    </div>

                    <div className="form-divider">
                      <span>{loginMessage('divider', 'or')}</span>
                    </div>

                    <FormGrid className="auth-form__grid">
                      <FormField
                        label={loginMessage('form.email.label', 'Email')}
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        required
                        hideLabel
                        placeholder={loginMessage(
                          'form.email.placeholder',
                          'Email',
                        )}
                        showRequiredAsterisk
                        asteriskPosition="left"
                      />

                      <FormField
                        label={loginMessage('form.password.label', 'Password')}
                        name="password"
                        type="password"
                        passwordToggle
                        showPasswordLabel={loginMessage(
                          'form.password.showToggle',
                          'Show password',
                        )}
                        hidePasswordLabel={loginMessage(
                          'form.password.hideToggle',
                          'Hide password',
                        )}
                        autoComplete="current-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        required
                        hideLabel
                        placeholder={loginMessage(
                          'form.password.placeholder',
                          'Password',
                        )}
                        showRequiredAsterisk
                        asteriskPosition="left"
                      />
                    </FormGrid>

                    <div className="text-center mt-2">
                      <Link
                        href={buildAuthHref('/forgot-password', redirectTarget)}
                        className="custom-btn-link p-0"
                      >
                        {loginMessage('forgot_password', 'Forgot password?')}
                      </Link>
                    </div>

                    {errorMessage ? (
                      <Message tone="error" className="mt-3">
                        {errorMessage}
                      </Message>
                    ) : null}

                    <div className="d-grid mt-3">
                      <button
                        type="submit"
                        className="app-button app-button--primary login-cta gap-1"
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? loginMessage('form.submitting', 'Signing In...')
                          : loginMessage(
                              'form.submit',
                              'Continue to Your Account',
                            )}
                        {isSubmitting ? (
                          <span
                            className="spinner-border spinner-border-sm ms-1"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <ChevronRight size={14} strokeWidth={1.5} />
                        )}
                      </button>
                    </div>

                    <p className="microcopy small mt-2">
                      {loginMessage(
                        'microcopy',
                        'Securely continue to your account.',
                      )}
                    </p>
                  </fieldset>
                </form>

                <p className="register-text">
                  <span>{loginMessage('register_text', 'New Customer?')}</span>{' '}
                  <Button
                    as={Link}
                    href={buildAuthHref('/register', redirectTarget)}
                    variant="link"
                    className="custom-btn-link p-0 ms-1"
                  >
                    {loginMessage('register_link', 'Register')}
                  </Button>
                  .
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
